import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  Box,
  Typography,
  Popover,
  TextField,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ArrowDropDown as ArrowDropDownIcon,
  Search as SearchIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useInvestigation } from '../../investigation/context/InvestigationContext';
import { Customer } from '../../../types';
import { viewportGutterMaxWidth } from '../../../shared/constants/layout';

interface CustomerSwitcherProps {
  selectedCustomer: Customer;
}

const CustomerSwitcher: React.FC<CustomerSwitcherProps> = ({ selectedCustomer }) => {
  const navigate = useNavigate();
  const { customers, loadCustomers } = useInvestigation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [query, setQuery] = useState('');
  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setQuery('');
    setAnchorEl(event.currentTarget);
    loadCustomers();
  };

  const handleClose = () => setAnchorEl(null);

  const handleSelect = (customer: Customer) => {
    handleClose();
    if (customer.id !== selectedCustomer.id) {
      // URL is the single source of truth for the active customer; the dashboard
      // loads it from the route param. Pre-writing selectedCustomer here races
      // the navigation and triggers a redundant reload of the previous customer.
      navigate(`/customers/${customer.id}`);
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? customers.filter((c) => c.name.toLowerCase().includes(q) || c.id.toLowerCase().includes(q))
      : customers;
    return [...list].sort((a, b) => a.name.localeCompare(b.name));
  }, [customers, query]);

  return (
    <>
      <Tooltip title="Switch customer" placement="bottom">
        <IconButton
          size="small"
          onClick={handleOpen}
          aria-label="switch customer"
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          sx={{ mr: 2, ml: 0.25, borderRadius: 1, p: 0.25 }}
        >
          <ArrowDropDownIcon />
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { width: 360, maxWidth: viewportGutterMaxWidth, mt: 0.5, overflow: 'hidden' } } }}
      >
        <Box sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Switch customer
          </Typography>
        </Box>

        <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <TextField
            fullWidth
            size="small"
            autoFocus
            placeholder="Search customers…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <List sx={{ maxHeight: 360, overflow: 'auto', py: 0 }}>
          {filtered.map((customer) => {
            const isCurrent = customer.id === selectedCustomer.id;
            return (
              <ListItemButton
                key={customer.id}
                selected={isCurrent}
                onClick={() => handleSelect(customer)}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  {isCurrent && <CheckIcon fontSize="small" color="primary" />}
                </ListItemIcon>
                <ListItemText
                  primary={customer.name}
                  secondary={customer.id}
                  primaryTypographyProps={{ noWrap: true }}
                  secondaryTypographyProps={{
                    noWrap: true,
                    sx: { fontFamily: 'monospace', fontSize: '0.7rem' },
                  }}
                />
              </ListItemButton>
            );
          })}

          {filtered.length === 0 && (
            <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {query ? `No customers match “${query}”` : 'No customers available'}
              </Typography>
            </Box>
          )}
        </List>
      </Popover>
    </>
  );
};

export default CustomerSwitcher;
